import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { PALMENT_PROGRAM_ID, sendMemoTx } from '../lib/solanaTx';

const VaultContext = createContext(null);

const STORAGE_KEY = 'palment_vault_state_v1';

// Baseline synthetic protocol data so the dashboard feels alive on first load.
const INITIAL_TVL = 8_420_360;
const INITIAL_APY = 12.4;
const EXCHANGE_RATE = 0.985;

function safeStorageGet() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.warn('[palment] failed to read localStorage vault state', err);
    return null;
  }
}

function safeStorageSet(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    console.warn('[palment] failed to write localStorage vault state', err);
  }
}

function generateHistory(points = 30, base = INITIAL_TVL) {
  const arr = [];
  let v = base;
  for (let i = points - 1; i >= 0; i--) {
    const drift = (Math.random() - 0.48) * base * 0.015;
    v = Math.max(base * 0.6, v + drift);
    const d = new Date();
    d.setDate(d.getDate() - i);
    arr.push({
      date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      tvl: Math.round(v),
      apy: +(INITIAL_APY + (Math.random() - 0.5) * 2).toFixed(2),
    });
  }
  return arr;
}

function getInitialState() {
  const stored = safeStorageGet();
  return {
    pusdBalance: stored?.pusdBalance ?? 0,
    ypusdBalance: stored?.ypusdBalance ?? 0,
    tvl: stored?.tvl ?? INITIAL_TVL,
    apy: stored?.apy ?? INITIAL_APY,
    history: stored?.history ?? generateHistory(),
    txs: stored?.txs ?? [],
    hasFaucetClaimed: !!stored?.hasFaucetClaimed,
  };
}

export function VaultStateProvider({ children }) {
  const initial = useMemo(getInitialState, []);
  const [pusdBalance, setPusdBalance] = useState(initial.pusdBalance);
  const [ypusdBalance, setYpusdBalance] = useState(initial.ypusdBalance);
  const [tvl, setTvl] = useState(initial.tvl);
  const [apy, setApy] = useState(initial.apy);
  const [history, setHistory] = useState(initial.history); // eslint-disable-line no-unused-vars
  const [txs, setTxs] = useState(initial.txs);
  const [hasFaucetClaimed, setHasFaucetClaimed] = useState(initial.hasFaucetClaimed);

  // Real Solana wallet + connection. When `wallet.connected` is true, we
  // trigger on-chain memo transactions for deposit & faucet. Demo mode
  // never activates these (it never calls wallet-adapter's connect()).
  const { connection } = useConnection();
  const wallet = useWallet();

  // Ref mirror of pusdBalance so async actions can validate against the
  // freshest balance without adding the balance to their dep array.
  const pusdBalanceRef = useRef(pusdBalance);
  useEffect(() => {
    pusdBalanceRef.current = pusdBalance;
  }, [pusdBalance]);

  // Persist any state change.
  useEffect(() => {
    safeStorageSet({ pusdBalance, ypusdBalance, tvl, apy, history, txs, hasFaucetClaimed });
  }, [pusdBalance, ypusdBalance, tvl, apy, history, txs, hasFaucetClaimed]);

  // Background tick — drifts APY, accrues yPUSD, wobbles TVL.
  useEffect(() => {
    const id = setInterval(() => {
      setApy((prev) => {
        const next = +(prev + (Math.random() - 0.5) * 0.08).toFixed(2);
        return Math.max(8, Math.min(18, next));
      });
      setYpusdBalance((y) => +(y * 1.00002).toFixed(6));
      setTvl((t) => Math.round(t + (Math.random() - 0.48) * 1500));
    }, 7000);
    return () => clearInterval(id);
  }, []);

  const addTx = useCallback((tx) => {
    setTxs((list) =>
      [
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          time: new Date().toISOString(),
          ...tx,
        },
        ...list,
      ].slice(0, 20)
    );
  }, []);

  /**
   * Claim 1000 Mock PUSD. If a real wallet is connected we first send a memo
   * tx on devnet (`PALMENT:FAUCET:1000PUSD`). Demo mode skips the tx.
   */
  const claimFaucet = useCallback(async () => {
    let signature = null;
    if (wallet.connected && wallet.publicKey) {
      signature = await sendMemoTx({
        connection,
        wallet,
        memo: 'PALMENT:FAUCET:1000PUSD',
      });
    }
    setPusdBalance((b) => +(b + 1000).toFixed(4));
    setHasFaucetClaimed(true);
    addTx({ kind: 'faucet', pusd: 1000, ypusd: 0, signature });
    return { signature };
  }, [addTx, wallet, connection]);

  /**
   * Deposit `amount` PUSD. With a real wallet, signs a memo tx
   * (`PALMENT:DEPOSIT:<amount>PUSD`) and waits for devnet confirmation
   * before mutating state. Demo mode mutates state immediately.
   *
   * Resolves to `{ ok: true, ypusdOut, signature }` on success.
   * Resolves to `{ ok: false, reason }` on pre-flight validation failure.
   * Rejects if the wallet rejects or the network fails (caller shows toast).
   */
  const deposit = useCallback(
    async (amount) => {
      if (amount <= 0) return { ok: false, reason: 'ZERO_AMOUNT' };
      if (amount > pusdBalanceRef.current) {
        return { ok: false, reason: 'INSUFFICIENT_BALANCE' };
      }

      let signature = null;
      if (wallet.connected && wallet.publicKey) {
        signature = await sendMemoTx({
          connection,
          wallet,
          memo: `PALMENT:DEPOSIT:${amount}PUSD`,
        });
      }

      // Apply the state changes using functional setters so background
      // tick updates can't race with us.
      let accepted = false;
      setPusdBalance((b) => {
        if (amount > b) return b;
        accepted = true;
        return +(b - amount).toFixed(6);
      });
      if (!accepted) {
        // Extremely unlikely: balance changed between validation and
        // commit. Surface it — the memo tx already landed on-chain.
        return { ok: false, reason: 'RACE_CONDITION', signature };
      }
      const ypusdOut = +(amount * EXCHANGE_RATE).toFixed(6);
      setYpusdBalance((y) => +(y + ypusdOut).toFixed(6));
      setTvl((t) => t + amount);
      addTx({ kind: 'deposit', pusd: amount, ypusd: ypusdOut, signature });
      return { ok: true, ypusdOut, signature };
    },
    [addTx, wallet, connection]
  );

  /**
   * Withdraw stays simulated (not requested for on-chain wiring yet).
   */
  const withdraw = useCallback(
    (yAmount) => {
      let pusdOut = 0;
      let accepted = false;
      setYpusdBalance((y) => {
        if (yAmount <= 0 || yAmount > y) return y;
        accepted = true;
        pusdOut = +((yAmount / EXCHANGE_RATE) * 1.015).toFixed(6);
        return +(y - yAmount).toFixed(6);
      });
      if (!accepted) return false;
      setPusdBalance((b) => +(b + pusdOut).toFixed(6));
      setTvl((t) => Math.max(0, t - pusdOut));
      addTx({ kind: 'withdraw', pusd: pusdOut, ypusd: yAmount });
      return { pusdOut };
    },
    [addTx]
  );

  const previewDeposit = useCallback(
    (amount) => +((Number(amount) || 0) * EXCHANGE_RATE).toFixed(6),
    []
  );

  const previewWithdraw = useCallback(
    (yAmount) => +((Number(yAmount) || 0) / EXCHANGE_RATE * 1.015).toFixed(6),
    []
  );

  const value = useMemo(
    () => ({
      pusdBalance,
      ypusdBalance,
      tvl,
      apy,
      history,
      txs,
      hasFaucetClaimed,
      claimFaucet,
      deposit,
      withdraw,
      previewDeposit,
      previewWithdraw,
      exchangeRate: EXCHANGE_RATE,
      programId: PALMENT_PROGRAM_ID,
    }),
    [
      pusdBalance,
      ypusdBalance,
      tvl,
      apy,
      history,
      txs,
      hasFaucetClaimed,
      claimFaucet,
      deposit,
      withdraw,
      previewDeposit,
      previewWithdraw,
    ]
  );

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>;
}

export function useVault() {
  const ctx = useContext(VaultContext);
  if (!ctx) throw new Error('useVault must be used inside VaultStateProvider');
  return ctx;
}
